// src/pages/aboutme/profile.data.ts

// ===== Tipagens =====
export interface SocialLink {
  label: "Email" | "LinkedIn" | "GitHub" | "Site" | "WhatsApp" | string;
  url: string;
}

export interface ExperienceItem {
  role: string;
  company: string;
  location?: string;
  start: string; // "jun de 2022"
  end: string; // "Presente" | "jun de 2024" etc.
  summary?: string;
  bullets?: string[];
  stack?: string[];
}

export interface EducationItem {
  school: string;
  degree: string;
  start?: string;
  end?: string;
  details?: string[];
}

export interface CertificationItem {
  name: string;
  org: string;
  year?: string;
  url?: string;
}

export interface ProjectItem {
  name: string;
  acronym?: string;
  role?: string;
  period?: string;
  description?: string;
  techs?: string[];
  url?: string;
}

export interface ProfileData {
  fullName: string;
  headline: string;
  about: string[];
  socials: SocialLink[];
  skills: {
    backend: { name: string; level: number }[];
    databases: { name: string; level: number }[];
    orm: { name: string; level: number }[];
    linguagens: { name: string; level: number }[];
    others: string[];
  };
  experience: ExperienceItem[];
  education: EducationItem[];
  certifications: CertificationItem[];
  projects: ProjectItem[];
}

// ===== Dados =====
export const profileData: ProfileData = {
  fullName: "RICARDO VILELA",
  headline: "Desenvolvedor de Software",
  about: [
    "Desenvolvedor Full Stack com sólida experiência em backend e frontend.",
    "Foco em aplicações robustas, escaláveis e arquitetura de código limpo.",
    "Especialista em Java/Spring Boot e Node.js/NestJS, com forte domínio de TypeScript e bancos de dados relacionais."
  ],
  socials: [
    { label: "Email", url: "mailto:dev.rmvnew@gmail.com" },
    {
      label: "LinkedIn",
      url: "https://www.linkedin.com/in/ricardo-mangabeiro/"
    },
    { label: "GitHub", url: "https://github.com/rmvnew" }
    // { label: "WhatsApp", url: "https://wa.me/5592992720266" }
  ],
  skills: {
    backend: [
      { name: "Java/Spring Boot", level: 97 },
      { name: "Node.js/NestJS", level: 90 },
      { name: "TypeScript", level: 92 }
    ],
    databases: [
      { name: "PostgreSQL", level: 88 },
      { name: "MySQL", level: 94 },
      { name: "SQL Server", level: 91 }
    ],
    orm: [
      { name: "TypeORM", level: 96 },
      { name: "Prisma", level: 90 },
      { name: "Hibernate", level: 88 }
    ],
    linguagens: [
      { name: "Java", level: 97 },
      { name: "TypeScript", level: 92 },
      { name: "JavaScript", level: 95 },
      { name: "Python", level: 85 },
      { name: "C", level: 90 },
      { name: "Kotlin", level: 75 }
    ],
    others: ["Docker", "CI/CD", "Microsserviços", "Segurança"]
  },

  experience: [
    {
      role: "Desenvolvedor Full Stack Sr",
      company: "Instituto Conecthus - Tecnologia e Biotecnologia do Amazonas",
      location: "Manaus, Amazonas, Brasil",
      start: "jun de 2022",
      end: "Presente",
      summary:
        "Atuação em projetos web, APIs e microsserviços com foco em escalabilidade.",
      stack: ["NestJS", "Spring Boot", "TypeScript", "Java", "Docker"]
    },
    {
      role: "Desenvolvedor Full Stack Pleno",
      company: "Instituto Conecthus - Tecnologia e Biotecnologia do Amazonas",
      location: "Manaus, Amazonas, Brasil",
      start: "set de 2020",
      end: "jun de 2022",
      summary:
        "Desenvolvimento de APIs para projetos web utilizando TypeScript/NestJS e Java/Spring.",
      stack: ["NestJS", "Spring Boot", "TypeScript", "Java"]
    },
    {
      role: "Desenvolvedor Full Stack Jr",
      company: "Instituto Conecthus - Tecnologia e Biotecnologia do Amazonas",
      location: "Manaus, Amazonas, Brasil",
      start: "jan de 2020",
      end: "set de 2020",
      summary:
        "SDK para POS; desenvolvimento Android nativo (Java/Kotlin) com MVVM; integração de API de pagamento Celer.",
      stack: ["Java", "Kotlin", "Android", "SDK", "MVVM"]
    },
    {
      role: "Técnico Desenvolvedor Pleno",
      company: "Instituto Conecthus - Tecnologia e Biotecnologia do Amazonas",
      location: "Manaus, Amazonas, Brasil",
      start: "mar de 2018",
      end: "dez de 2019",
      summary:
        "Projetos embarcados e soluções para gerenciamento/controle de hardware e testes, majoritariamente em Java.",
      stack: ["Java", "Linux", "Sistemas embarcados"]
    }
  ],

  education: [
    {
      school: "UniNorte (Centro Universitário do Norte)",
      degree:
        "Curso Superior de Tecnologia (CST), Análise e Desenvolvimento de Sistemas",
      start: "jul de 2022",
      end: "dez de 2023"
    },
    {
      school: "FUCAPI",
      degree: "Ensino Técnico, Eletrônica Digital",
      start: "2011",
      end: "2012"
    }
  ],

  certifications: [
    {
      name: "Curso de Domain Driven Design",
      org: "Full Cycle",
      year: "jul de 2023"
    },
    {
      name: "Curso de Fundamentos da Arquitetura de Software",
      org: "Full Cycle",
      year: "set de 2022"
    },
    {
      name: "React: Desenvolvendo com JavaScript",
      org: "Alura",
      year: "ago de 2022"
    },
    {
      name: "Curso de Docker",
      org: "Full Cycle",
      year: "jul de 2023"
    },
    {
      name: "Curso de Java 8",
      org: "Alura",
      year: "jul de 2018"
    },
    {
      name: "Kotlin: herança, polimorfismo e Interface (10h)",
      org: "Alura",
      year: "2020"
    },
    {
      name: "Android parte 1: Crie a sua primeira app mobile (10h)",
      org: "Alura",
      year: "2020"
    },
    {
      name: "Python 3 parte 1: Introdução à nova versão da linguagem (12h)",
      org: "Alura",
      year: "2020"
    },
    {
      name: "Kotlin: primeiros passos e Orientação a Objetos (10h)",
      org: "Alura",
      year: "2020"
    },
    {
      name: "Spring Boot API REST: Construa uma API (8h)",
      org: "Alura",
      year: "2019"
    }
  ],

  // ====== PROJETOS (sem o Roleplay) ======
  projects: [
    {
      name: "LMS – Lean Manufacturing System - PROJECT",
      acronym: "LMS",
      period: "2025 – Atual",
      description:
        "Plataforma de manufatura enxuta para controle de processos industriais, métricas de produção e gestão de indicadores em tempo real.",
      techs: ["NestJS", "React", "Prisma", "RabbitMQ", "SQL-Server", "Docker"]
    },
    {
      name: "LMS – Lean Manufacturing System - TRAINING",
      acronym: "LMS",
      period: "2025 – 2024",
      description:
        "Plataforma de manufatura enxuta, com painéis, métricas e processos para gestão de chão de fábrica.",
      techs: ["NestJS", "React", "Prisma", "RabbitMQ", "SQL-Server", "Docker"]
    },
    {
      name: "SICRAM – Sistema Inteligente de Conferência e Recebimento de Armazém",
      acronym: "SICRAM",
      period: "2024 – 2025",
      description:
        "Sistema para conferência, recebimento e rastreabilidade de materiais em armazém.",
      techs: ["Java", "Spring Boot", "NestJS", "SQL-Server", "Docker"]
    },
    {
      name: "E-PRENATAL",
      period: "2024 – 2024",
      description:
        "Sistema voltado à área de saúde para registro, acompanhamento e relatórios de atendimentos pré-natal.",
      techs: ["NestJS", "TypeScript", "React", "MySQL", "Docker"]
    },

    {
      name: "Integração MPS",
      period: "2023 – 2024",
      description:
        "Serviços de integração MPS para sincronização de dados e automações entre sistemas corporativos.",
      techs: ["NestJS", "TypeScript", "REST/Queues", "SQL-Server"]
    },
    {
      name: "MSL – Moisture Sensitivity Level",
      acronym: "MSL",
      period: "2023 – Presente",
      description:
        "Aplicação para acompanhamento de nível de sensibilidade à umidade em componentes e processos.",
      techs: ["NestJS", "React", "TypeScript", "SQL-Server"]
    },

    {
      name: "SIMIP – Sistema Integrado de Monitoramento Inteligente de Pragas",
      acronym: "SIMIP",
      period: "2024 – 2025",
      description:
        "Monitoramento inteligente de pragas com coleta de dados e dashboards de acompanhamento.",
      techs: ["NestJS", "React", "TypeScript", "MySQL", "Docker"]
    },
    {
      name: "RMS ADATA",
      acronym: "RMS",
      period: "2023 – 2023",
      description:
        "Solução de gestão de projetos de software administrados pela equipe (RMS ADATA).",
      techs: ["NestJS", "React", "TypeScript", "SQL-Server", "Docker"]
    }
  ]
};
